import {
  ErrorList,
  getFontCss,
  HoverableLinkSizes,
  Popup,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  autoMatchReceipts,
  createAutomatchReceiptResetAction,
  createSetReceiptAutoMatchAttachmentIds,
} from 'src/app/UploadReceiptsAutoMatchPopup/redux/uploadReceiptsAutoMatch.actions'
import {
  selectReceiptAttachmentModelsList,
  selectUploadingAttachmentsFromState,
} from 'src/app/UploadReceiptsAutoMatchPopup/redux/uploadReceiptsAutoMatch.selectors'
import AttachmentsList from 'src/components/AttachmentsList/AttachmentsList'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import SvgArrowForward from 'src/components/Svgs/SvgArrowForward'
import SvgReceiptUpload from 'src/components/Svgs/SvgReceiptUpload'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import { svgImageNames } from 'src/enums'
import { useColorOrGrayscaleSVG } from 'src/hooks/brandHooks'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import { connectedPush } from 'src/navigation/Lnk'
import { myWalletReimbursementURL } from 'src/navigation/urls'
import { useUploadReceiptAttachments } from 'src/queries/receiptAttachments/receiptAttachments.queries'
import { createSetIsCreateExpenseReportPopupOpen } from 'src/redux/actions/app.actions'
import {
  postCreateExpenseReport,
  SET_EMPTY_STATE_EXPENSE_REPORTS,
} from 'src/redux/actions/data/expenses.actions'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import {
  selectLatestExpenseReportsListModelFromState,
  selectPostingExpenseErrorFromState,
  selectPostingExpenseFromState,
} from 'src/redux/selectors/data/expenses.selectors'
import { PostReceiptAttachmentRequest } from 'src/requests/postReceiptAttachment.request'
import { css } from 'styled-components'

const CreateExpenseReportPopup: React.FC = (): React.ReactElement => {
  const translate = useTranslate()

  const dispatch = useDispatch()
  const uploadReceiptAttachments = useUploadReceiptAttachments({
    dispatchMode: 'redux',
  })
  const locale = useSelector(selectNullSafeLocaleFromState)
  const postError = useSelector(selectPostingExpenseErrorFromState)
  const loading = useSelector(selectPostingExpenseFromState)

  const receiptAttachments = useSelector(selectReceiptAttachmentModelsList)
  const uploadingAttachmentThumbnails = useSelector(
    selectUploadingAttachmentsFromState,
  )
  const latestExpenseReport = useSelector(
    selectLatestExpenseReportsListModelFromState,
  )
  const expenseTopRight = useColorOrGrayscaleSVG(svgImageNames.ExpenseTopRight)

  const [attachmentError, setAttachmentError] = useState<
    string | React.ReactElement | undefined
  >()

  const [expenseReportName, setExpenseReportName] = useState<string>('')
  const [page, setPage] = useState(0)

  const dispatchUploadAttachments = (
    attachments: PostReceiptAttachmentRequest[],
  ): void => {
    uploadReceiptAttachments.mutate(attachments)
  }

  const deleteAttachment = (attachmentId: string): void => {
    dispatch(
      createSetReceiptAutoMatchAttachmentIds(
        receiptAttachments
          .filter((att) => att.id !== attachmentId)
          .map((att) => att.id),
      ),
    )
  }

  const closePopup = (): void => {
    dispatch(createSetIsCreateExpenseReportPopupOpen(false))
  }

  const handleCreateExpenseReport = (): void => {
    if (!loading && expenseReportName.length > 0) {
      dispatch(postCreateExpenseReport(expenseReportName))
      dispatch({ type: SET_EMPTY_STATE_EXPENSE_REPORTS })
      setPage(1)
    }
  }

  const handleViewExpenseReport = (): void => {
    if (latestExpenseReport === undefined) {
      return
    }
    if (receiptAttachments.length > 0) {
      const receiptIds = receiptAttachments.map((attachment) => attachment.id)
      dispatch(autoMatchReceipts(receiptIds, latestExpenseReport.id))
    } else {
      dispatch(createSetIsCreateExpenseReportPopupOpen(false))
      dispatch(connectedPush(myWalletReimbursementURL(latestExpenseReport.id)))
    }
  }

  useEffect(() => {
    dispatch(createAutomatchReceiptResetAction())
  }, [])

  return (
    <Popup
      onClickClose={closePopup}
      showCloseButton
      css={css`
        display: flex;
        position: relative;
      `}
    >
      {expenseTopRight}
      {page === 0 && (
        <div
          css={css`
            width: 100%;
            display: flex;
            flex-direction: column;
            row-gap: 40px;
          `}
        >
          <span
            css={css`
              ${getFontCss(
                32,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )};
            `}
          >
            {translate('Name Your Reimbursement Report')}
          </span>
          <span
            css={css`
              ${getFontCss(
                16,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )};
            `}
          >
            {translate(
              'Create a reimbursement report to get reimbursed for your expenses. Give your report a name that will tie your expenses together.',
            )}
          </span>
          <div>
            <TextInputField
              design={TextInputFieldDesigns.New}
              fieldLabel={<></>}
              value={expenseReportName}
              placeholder={translateSingleString(locale, 'Untitled report')}
              errors={postError ? [postError] : undefined}
              onChange={(val: string): void => {
                setExpenseReportName(val)
              }}
              characterLimit={40}
            />
          </div>
          <div
            css={css`
              display: flex;
              justify-content: end;
            `}
          >
            <HoverableLink
              onClick={handleCreateExpenseReport}
              regular
              disabled={loading || expenseReportName.length === 0}
              ariaLabel={'Create Report and Add Expenses'}
              size={HoverableLinkSizes.Large}
              colorSvgOnHover
            >
              <span
                css={css`
                  display: flex;
                  align-items: center;
                  ${getFontCss(
                    14,
                    themeConstants.colors.cta,
                    styleConstants.fontFamilies.medium,
                  )};
                `}
              >
                {translate('Create Report and Add Expenses')}
                <SvgArrowForward
                  css={css`
                    transform: scale(calc(18 / 24));
                  `}
                />
              </span>
            </HoverableLink>
          </div>
        </div>
      )}
      {page === 1 && (
        <div
          css={css`
            width: 100%;
            display: flex;
            flex-direction: column;
          `}
        >
          <span
            css={css`
              margin-bottom: 10px;
              ${getFontCss(
                32,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )};
            `}
          >
            {translate('Add Your Expenses')}
          </span>
          <span
            css={css`
              margin-bottom: 40px;
              ${getFontCss(
                16,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )};
            `}
          >
            {translate(
              'Upload receipts to quickly create expenses for your reimbursement report. You can upload up to 10 receipts at a time.',
            )}
          </span>
          <div
            css={css`
              margin-bottom: 30px;
            `}
          >
            <AttachmentsList
              attachments={receiptAttachments}
              uploadingAttachmentThumbnails={uploadingAttachmentThumbnails}
              uploadAttachments={dispatchUploadAttachments}
              deleteAttachment={deleteAttachment}
              isReceiptRequired={false}
              setAttachmentError={setAttachmentError}
              attachmentLimit={10}
              quickDeleteIcon
              attachmentsText={
                <>
                  <SvgReceiptUpload
                    css={css`
                      transform: scale(1.6);
                      margin-bottom: 10px;
                    `}
                  />
                  <span
                    css={css`
                      ${getFontCss(
                        14,
                        themeConstants.colors.cta,
                        styleConstants.fontFamilies.medium,
                      )};
                    `}
                  >
                    {translate('Upload Receipts to Generate Expenses')}
                  </span>
                </>
              }
            />
          </div>
          {receiptAttachments.length === 0 && (
            <div
              css={css`
                display: flex;
                flex-direction: row;
                align-items: center;
                margin-bottom: 30px;
              `}
            >
              <div
                css={css`
                  height: 1px;
                  background: #eaebf1;
                  flex-grow: 1;
                  margin-right: 16px;
                `}
              ></div>
              {translate('or')}
              <div
                css={css`
                  height: 1px;
                  background: #eaebf1;
                  flex-grow: 1;
                  margin-left: 16px;
                `}
              ></div>
            </div>
          )}
          <div
            css={css`
              display: flex;
              justify-content: ${receiptAttachments.length > 0
                ? 'end'
                : 'center'};
            `}
          >
            <HoverableLink
              onClick={handleViewExpenseReport}
              regular
              ariaLabel={'Add Expenses Manually'}
              size={HoverableLinkSizes.Large}
              colorSvgOnHover
              testId="add-expenses-manually"
            >
              <span
                css={css`
                  display: flex;
                  align-items: center;
                  ${getFontCss(
                    14,
                    themeConstants.colors.cta,
                    styleConstants.fontFamilies.medium,
                  )};
                `}
              >
                {translate(
                  receiptAttachments.length > 0
                    ? 'Create Report and Review Expenses'
                    : 'Add Expenses Manually',
                )}
                <SvgArrowForward
                  css={css`
                    transform: scale(calc(18 / 24));
                  `}
                />
              </span>
            </HoverableLink>
            <div
              css={css`
                display: flex;
                flex-direction: column;
              `}
            >
              {attachmentError && (
                <ErrorList
                  errors={[attachmentError || <></>]}
                  css={css`
                    padding-left: 0;
                  `}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </Popup>
  )
}

export default CreateExpenseReportPopup
